import { useInfiniteQuery } from '@tanstack/react-query'
import api from 'api/api'
import { accessTokens } from 'api/accessTokenMgmt'
import { STREAMS_QUERY_POLICY } from 'api/queryPolicies'
import { linkedFeedQueryKey } from 'lib/feedPagination'

export interface StreamWithActivity {
    id: string
    name: string
    last_activity?: string
    quest_count?: number
    public?: boolean
    // Add other ITeam properties as needed
}

interface GetStreamsResponse {
    teams: StreamWithActivity[]
    has_more: boolean
    next_page?: number
    total_count: number
}

const fetchStreams = async ({ pageParam = 1 }): Promise<GetStreamsResponse> => {
    const response = await fetch(`${window.knovApiUrl}/api/v1/streams?page=${pageParam}`, {
        headers: {
            ...api.commonHeaders(),
            ...accessTokens(),
            'X-CSRF-Token': gon.formToken,
        },
        credentials: 'same-origin',
    })

    if (!response.ok) {
        throw new Error('Failed to fetch channels')
    }

    const data = await response.json()
    return {
        teams: data.teams || [],
        has_more: data.has_more || false,
        next_page: data.next_page,
        total_count: data.total_count || 0,
    }
}

export const useGetStreams = (
    enabled = true,
    { initialPage = 1 }: { initialPage?: number } = {},
) => {
    const query = useInfiniteQuery({
        // Hard loads of /streams?page=N (crawlers, deep links) start at that
        // batch; key by it so page-1 and page-N caches stay distinct.
        queryKey: linkedFeedQueryKey(['streams'], initialPage),
        queryFn: fetchStreams,
        enabled,
        staleTime: STREAMS_QUERY_POLICY.staleTime,
        gcTime: STREAMS_QUERY_POLICY.gcTime,
        retry: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        structuralSharing: false,
        networkMode: 'always',
        initialPageParam: initialPage,
        getNextPageParam: lastPage => (lastPage.has_more ? lastPage.next_page : undefined),
    })

    // Flatten all pages into a single array of streams
    const streams = query.data?.pages.flatMap(page => page.teams) || []

    // The next linked-pagination page (?page=N), when the server says more exist.
    const lastLoadedPage = query.data?.pages[query.data.pages.length - 1]
    const nextPage = lastLoadedPage?.has_more ? lastLoadedPage.next_page : undefined

    return {
        streams,
        fetchNextPage: query.fetchNextPage,
        hasNextPage: query.hasNextPage,
        nextPage,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
        isLoading: query.isLoading,
        refetch: query.refetch,
        totalStreams: query.data?.pages[0]?.total_count || 0,
    }
}
